import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Mail, Lock, Eye, EyeOff, Shield } from "lucide-react";
import { backendApi } from "@/api/backendApi";
import { GoogleLogin } from "@react-oauth/google";

export default function LoginDrawer({ isOpen, onClose }) {
  const [mode, setMode] = useState("password"); // "password" | "otp-send" | "otp-verify"
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const clearMessages = () => { setError(""); setSuccess(""); };

  // Password login
  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const data = await backendApi.auth.login(email, password);
      onClose();
      if (data.role === "employer") {
        navigate("/recruit/dashboard");
      } else {
        navigate("/jobs/dashboard");
      }
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  // Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!email) { setError("Please enter your email"); return; }
    setLoading(true);
    try {
      const result = await backendApi.auth.sendOTP(email);
      setSuccess(result.otp ? `Dev OTP: ${result.otp}` : "OTP sent to your email!");
      setMode("otp-verify");
    } catch (err) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const data = await backendApi.auth.verifyOTP(email, otp);
      onClose();
      if (data.role === "employer") {
        navigate("/recruit/dashboard");
      } else {
        navigate("/jobs/dashboard");
      }
    } catch (err) {
      setError(err.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // Google login handler
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      clearMessages();
      setLoading(true);
      const data = await backendApi.auth.googleLogin(credentialResponse.credential);
      onClose();
      if (data.role === "employer") {
        navigate("/recruit/dashboard");
      } else {
        navigate("/jobs/dashboard");
      }
    } catch (err) {
      setError(err.message || "Google Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="relative w-full max-w-[400px] h-full bg-white shadow-2xl p-8 overflow-y-auto animate-in slide-in-from-right duration-300">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-6 h-6" />
        </button>

        <div className="mt-8">
          {/* Header */}
          <div className="flex justify-between items-baseline mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              {mode === "password" ? "Login" : mode === "otp-send" ? "Login with OTP" : "Verify OTP"}
            </h2>
            <Link to="/jobs/auth" onClick={onClose} className="text-sm text-[#2557a7] hover:underline font-semibold">
              Register for free
            </Link>
          </div>

          {/* Error / Success */}
          {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-2.5 mb-4">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-600 text-sm rounded-lg px-4 py-2.5 mb-4">{success}</div>}

          {/* ── PASSWORD LOGIN ── */}
          {mode === "password" && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Email ID</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full h-10 pl-10 pr-12 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button type="button" className="text-xs text-[#2557a7] hover:underline">Forgot Password?</button>
              </div>

              <button type="submit" disabled={loading} className="w-full bg-[#2557a7] text-white font-semibold h-11 rounded-full hover:bg-[#1e4687] disabled:opacity-50 transition-colors">
                {loading ? "Logging in..." : "Login"}
              </button>

              <div className="text-center">
                <button type="button" onClick={() => { clearMessages(); setMode("otp-send"); }} className="text-sm text-[#2557a7] hover:underline font-medium flex items-center gap-1.5 mx-auto">
                  <Shield className="w-4 h-4" /> Use OTP to Login
                </button>
              </div>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center"><span className="px-2 bg-white text-gray-400 text-sm">Or</span></div>
              </div>

              <div className="flex justify-center w-full min-h-[44px]">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError("Google login failed.")}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="pill"
                  width="100%"
                />
              </div>
            </form>
          )}

          {/* ── OTP SEND ── */}
          {mode === "otp-send" && (
            <form onSubmit={handleSendOTP} className="space-y-5">
              <p className="text-sm text-gray-500 mb-4">We'll send a 6-digit verification code to your email</p>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                  />
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full bg-[#2557a7] text-white font-semibold h-11 rounded-full hover:bg-[#1e4687] disabled:opacity-50 transition-colors">
                {loading ? "Sending..." : "Send OTP"}
              </button>

              <button type="button" onClick={() => { clearMessages(); setMode("password"); }} className="w-full text-sm text-[#2557a7] hover:underline font-medium">
                Back to Password Login
              </button>
            </form>
          )}

          {/* ── OTP VERIFY ── */}
          {mode === "otp-verify" && (
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <p className="text-sm text-gray-500 mb-4">Enter the 6-digit code sent to <strong>{email}</strong></p>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">OTP Code</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm tracking-[0.3em] text-center font-semibold"
                  />
                </div>
              </div>

              <button type="submit" disabled={loading || otp.length < 6} className="w-full bg-[#2557a7] text-white font-semibold h-11 rounded-full hover:bg-[#1e4687] disabled:opacity-50 transition-colors">
                {loading ? "Verifying..." : "Verify & Login"}
              </button>

              <div className="flex justify-between text-sm">
                <button type="button" onClick={handleSendOTP} className="text-[#2557a7] hover:underline font-medium">Resend OTP</button>
                <button type="button" onClick={() => { clearMessages(); setMode("password"); }} className="text-gray-500 hover:underline">Use Password</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
